import React, { createContext, ReactNode, useContext, useMemo } from "react";

import { useCompareTools, UseCompareToolsReturn } from "./useCompareTools";

/**
 * Context type for compare tools functionality
 */
type CompareContextType = UseCompareToolsReturn;

/**
 * Context for managing tool comparison state across components
 */
const CompareContext = createContext<CompareContextType | null>(null);

/**
 * Props for CompareProvider component
 */
interface CompareProviderProps {
  children: ReactNode;
}

/**
 * Provider component for compare tools context
 * Wraps the application to provide compare functionality
 */
export const CompareProvider: React.FC<CompareProviderProps> = ({ children }) => {
  const compareTools = useCompareTools();

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(() => compareTools, [compareTools]);

  return <CompareContext.Provider value={contextValue}>{children}</CompareContext.Provider>;
};

/**
 * Hook to access compare tools context
 * Must be used within a CompareProvider
 *
 * @returns Compare tools state and actions
 * @throws Error if used outside of CompareProvider
 */
export const useCompare = (): CompareContextType => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error("useCompare must be used within a CompareProvider");
  }
  return context;
};

/**
 * Hook to check if a specific tool is selected for comparison
 * Convenience hook that wraps useCompare
 *
 * @param taxonomyId - The taxonomy ID of the tool to check
 * @returns Whether the tool is selected
 */
export const useIsToolSelected = (taxonomyId: string): boolean => {
  const { isToolSelected } = useCompare();
  return isToolSelected(taxonomyId);
};

/**
 * Hook to get comparison state summary
 * Useful for components that only need read-only state
 */
export const useCompareState = () => {
  const { selectedTools, isFlyoutOpen, isReviewFlyoutOpen, canAddMore, canCompare, selectedCount, selectedToolIds } =
    useCompare();
  return {
    selectedTools,
    isFlyoutOpen,
    isReviewFlyoutOpen,
    canAddMore,
    canCompare,
    selectedCount,
    selectedToolIds,
  };
};

/**
 * Hook to get comparison actions only
 * Useful for components that only need to trigger actions
 */
export const useCompareActions = () => {
  const { addTool, removeTool, clearAll, openFlyout, closeFlyout, setReviewFlyoutOpen } = useCompare();
  return {
    addTool,
    removeTool,
    clearAll,
    openFlyout,
    closeFlyout,
    setReviewFlyoutOpen,
  };
};
